import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

export type Language = 'en' | 'et';

interface LanguageContextType {
  currentLanguage: Language;
  changeLanguage: (language: Language) => void;
  availableLanguages: Language[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const availableLanguages: Language[] = ['en', 'et'];
  const currentLanguage = (i18n.language as Language) || 'et';

  const changeLanguage = useCallback((language: Language) => {
    i18n.changeLanguage(language);
    
    // Update URL to reflect language change
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(en|et)/, '') || '/';
    const newPath = `/${language}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
    
    navigate(newPath, { replace: true });
  }, [i18n, navigate, location.pathname]);

  const contextValue: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    availableLanguages,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};
